import { rem } from 'polished'
import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Image from 'components/Common/Image'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import ContactContext from 'contexts/contactContext'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import GeoContext from 'contexts/geoContext'
import getCustomerSupportAgentImage from './CustomerSupport/getCustomerSupportAgentImage'

const IMAGE_WIDTH = 100

const Container = styled.div`
  display: grid;
  grid-template-columns: ${rem(IMAGE_WIDTH)} 1fr;
  grid-template-rows: 1fr;
  align-items: center;
  gap: ${rem(16)};
`

interface Props {
  heading?: string
  description?: string
}

function CustomerSupportBlock(props: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)

  const customerSupportImage = getCustomerSupportAgentImage('left', false, currentRegionCode)

  const availability7Days = intl.formatMessage({
    id: 'i0s1xr8sPw',
    defaultMessage: '7 days a week from 9am to 7pm',
    description: 'Availability schedule for regions without 24/7 support',
  })
  const availability247 = intl.formatMessage({
    id: 'JVakr21/cr',
    defaultMessage: '24 hours, 7 days',
    description: 'Availability schedule for regions with 24/7 support',
  })
  const availabilityText = NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) ? availability7Days : availability247

  const defaultHeading = intl.formatMessage(
    {
      id: 'v2R4plJnHF',
      defaultMessage: 'Our team are available {availability}',
      description: 'Customer support block heading with team availability',
    },
    { availability: availabilityText },
  )
  const defaultDescription = intl.formatMessage({
    id: 'lzJvzNP/Ok',
    defaultMessage: 'Need help? Call our dedicated customer service team anytime.',
    description: 'Customer support block description encouraging users to call',
  })

  const {
    heading = defaultHeading,
    description = defaultDescription,
  } = props

  const supportPhone = useContext(ContactContext)

  return (
    <Container>
      <Image
        alt={intl.formatMessage({
          id: 'IeuWGzCGnf',
          defaultMessage: 'Travel companion on your trip',
          description: 'Alt text for customer support agent image',
        })}
        image={customerSupportImage}
        width={IMAGE_WIDTH}
        fit="center"
      />
      <Group direction="vertical" gap={8}>
        <Heading variant="heading6">
          {heading}
        </Heading>
        <BodyText variant="medium">
          {description}
        </BodyText>
        <TextLink startIcon={<LinePhoneIcon />} href={`tel:${supportPhone.local.number}`}>
          {supportPhone.local.humanReadable}
        </TextLink>
      </Group>
    </Container>
  )
}

export default CustomerSupportBlock
